import { StoreModelCollection } from "@bitwarden/web-vault/app/services/store/model.collection.abstraction";
import { StoreModelNames } from "../../dali/type/dali.type";
import { computed, inject, signal } from "@angular/core";
import { TransactionStoreModel } from "../../../models/store/transaction.store.model";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { type IModelView } from "../../../models/view/model.view";

import { DependencyPublisher } from "@bitwarden/web-vault/app/services/store/dependency.publisher.abstraction";
import { repairDanglingReferences } from "../referential-repair";
import { Observable } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { createMutex } from "@bitwarden/web-vault/app/shared/utils/mutex";
import { ApmService } from "@elastic/apm-rum-angular";

/** Cap the ids listed in a load-duplicate report so a single APM event stays small (#5694). */
const DUPLICATE_ID_REPORT_CAP = 20;

/**
 * Thrown when a delete only partially completed — an earlier write committed but a later write in
 * the same logical delete failed. Two shapes: (a) a surviving partner's repair committed but the
 * target's delete write failed (target still present, see #4442); (b) the main transaction delete
 * committed but its source-book cleanup failed (target already gone, see #4504 item 9). Both are
 * transient half-states a retry resolves, so callers must treat this differently from a clean
 * failure (nothing persisted) — reporting it as an untouched no-op is wrong. The original failure is
 * preserved on `cause`.
 */
export class PartialTransactionDeleteError extends Error {
  constructor(readonly cause: unknown) {
    super("Transaction delete partially completed: a follow-up write did not finish");
    this.name = "PartialTransactionDeleteError";
  }
}

// todo - implement CategoryView not here

export class TransactionStoreCollection
  extends StoreModelCollection
  implements DependencyPublisher<TransactionView>
{
  readonly type: StoreModelNames = "TransactionStoreModel";
  protected models: Map<string, TransactionStoreModel> = new Map<string, TransactionStoreModel>();

  // Report load-time duplicates to APM (see loadModels) — `this.log` is ConsoleLogService(false) in
  // prod, so its duplicate report never leaves the user's browser. Mirrors StoreLoaderService (#5694).
  private apmService: ApmService = inject(ApmService);

  private _transactionViews = signal<TransactionView[]>(null);
  readonly transactionViews = this._transactionViews.asReadonly();

  // Serializes the link-state read-modify-writes on this collection against each other:
  // deleteWithLinkageRepair (snapshots surviving partners' lTid), the overridden saveToVault (through
  // which linkPair/unlink persist), and upsertPreservingLinkState (the Plaid in-place edit). Without
  // one shared lock, two of these that each read link state before writing race: the later write
  // clobbers the earlier one's change, re-stranding a dangling lTid or overwriting a user's mid-sync
  // unlink with a stale sync snapshot (#4442, PR #4783). DaliService's own mutex only serializes each
  // individual vault write, not a read-modify-write that spans a read + one or more writes — and it
  // cannot be reused here (the inner saves would deadlock on the same queue), so the collection keeps
  // its own mutex from the same factory (#4704 / #4873). NOTE: the single-view save() path is
  // deliberately NOT on this queue — it persists a caller-supplied view directly with no link-state
  // merge (CBAT rows, single-transaction edits), so it neither reads nor repairs lTid and has nothing
  // to serialize here.
  private readonly runMutationExclusive = createMutex();

  /** Store Subscriber/Publisher **/
  publisher$: Observable<TransactionView[]> = toObservable(this.transactionViews);

  /** First Transaction Date **/
  firstTransactionDate = computed(() => {
    return this.transactionViews()?.reduce(
      (acc, item) => (item.postDate.getTime() < acc.getTime() ? item.postDate : acc),
      new Date(),
    );
  });

  /** Last Transaction Date **/
  lastTransactionDate = computed(() => {
    return this.transactionViews()?.reduce(
      (acc, item) => (item.postDate.getTime() > acc.getTime() ? item.postDate : acc),
      new Date(0),
    );
  });

  triggerChanges(): void {
    this._transactionViews.set(this.buildViews(this.models, (model) => new TransactionView(model)));
  }

  /**
   * Delete a single transaction, repairing any surviving partner's `lTid`.
   * @see deleteWithLinkageRepair
   */
  override async delete(view: IModelView): Promise<boolean> {
    return this.deleteWithLinkageRepair([view]);
  }

  /**
   * Delete multiple transactions, repairing any surviving partner's `lTid`.
   * @see deleteWithLinkageRepair
   */
  override async deleteFromVault(views: IModelView[]): Promise<boolean> {
    return this.deleteWithLinkageRepair(views);
  }

  /**
   * Soft-delete the given transactions and strip the deleted ids from any *surviving*
   * transaction's `lTid`. The base collection only removes the target, leaving a linked partner
   * pointing at a transaction that no longer exists: the survivor renders with no link badge (its
   * partner is gone from `byId`) yet is still treated as a transfer by `setGlossBalance`
   * (`linkedTo.length > 0`), so it is silently netted out of income/expense and can never be
   * re-linked (`isAlreadyLinked`). Clearing the dangling id at delete time covers every caller in
   * one place. `xLid` (excludedLinkIds) is deliberately left as-is — stale ids there are harmless.
   * See issue #4442.
   *
   * The repair and the delete run as two separate vault writes, repair first: `commitUpdate`
   * re-batches the survivor's cipher and can move sibling rows (including the delete target) to a
   * fresh vid, so the delete must run against the models this write echoes back or it targets the
   * old cipher and the "deleted" row is silently re-created. A view with no stored model (e.g. a
   * synthesised `empty_account_balance` row) degrades to a no-op rather than throwing.
   */
  private async deleteWithLinkageRepair(views: IModelView[]): Promise<boolean> {
    // Serialize the whole read-modify-write, not just the vault writes below (see `runMutationExclusive`).
    // The snapshot of surviving partners is taken from `this.models` inside this exclusive block,
    // so it always reflects any prior mutation's committed state rather than a stale pre-write copy.
    return this.runMutationExclusive(async () => {
      const deletedIds = new Set(views.map((view) => view.id));

      // `lTid` is the first (and, today, only) referential field repaired on delete. Going through
      // the shared `repairDanglingReferences` rather than hand-rolling the scan here keeps the
      // referential-integrity-on-delete mechanism in one reusable place, so the next id-list field
      // that needs it (e.g. BudgetStoreModel.ctId) imports the same function instead of copying the
      // loop. See #4705.
      const repairedSurvivors = repairDanglingReferences(this.models.values(), "lTid", deletedIds);

      // The two writes are not atomic. If the delete write throws after the repair write has
      // committed, still refresh the view signal (finally) so it reflects the repaired survivor
      // rather than the stale pre-delete snapshot, and let the error propagate so the caller knows
      // the delete didn't land. The resulting persisted half-state (survivor unlinked, target still
      // present) is transient and resolves on retry. `mutated` keeps a pure no-op (nothing to repair
      // or delete) from rebuilding every view for no reason. `repairCommitted` lets the catch tell a
      // partial failure (survivor already unlinked) apart from a clean one (nothing persisted) so
      // callers don't report the partial case as an untouched no-op.
      let mutated = false;
      let repairCommitted = false;
      try {
        // Repair first, as its own write, then refresh *every* returned model — vids move when a
        // cipher is re-batched, so the delete below reads the up-to-date vid from `this.models`.
        if (repairedSurvivors.length) {
          // A repair spanning more than one cipher is itself non-atomic — `VaultWriter.commitUpdate`
          // loops per vid, so a mid-write failure can leave the first cipher's survivors already
          // unlinked. Flag it as committed *before* the write in that case: over-reporting a clean
          // failure as partial only costs a redundant retry, while under-reporting a real partial as
          // an untouched no-op is the exact miscategorisation this error exists to prevent.
          repairCommitted = new Set(repairedSurvivors.map((model) => model.vid)).size > 1;
          const savedModels = await this.daliService.save(this.type, repairedSurvivors);
          // Merge the echo through loadModels so it is validated exactly like every other insertion
          // path (save/saveToVault) instead of being stored raw: a missing/mismatched-field echo would
          // otherwise be merged unchecked and could later crash an unrelated triggerChanges() (#4700).
          // loadModels validates and skips an invalid echo. Refs #4699.
          this.loadModels(savedModels as TransactionStoreModel[]);
          mutated = true;
          repairCommitted = true;
        }

        // Map over the deduped `deletedIds` Set, not the raw `views`: a duplicate id in `views` would
        // otherwise queue the same model in `toDelete` twice, sending a redundant repeat of the same
        // row in the delete payload (#4701). filter(Boolean): a view with no stored model degrades to
        // the pre-#4442 no-op instead of throwing on `undefined.id`.
        const toDelete = [...deletedIds]
          .map((id) => this.models.get(id))
          .filter((model): model is TransactionStoreModel => Boolean(model));
        if (toDelete.length) {
          // The delete write is non-atomic for the same reason the repair above is —
          // `VaultWriter.commitDelete` loops per vid, so a mid-write failure can leave an earlier
          // cipher's rows already gone. `||=` so a committed repair still counts as partial. Single
          // cipher (size 1) keeps the clean-no-op semantics: an atomic failure rethrows raw.
          repairCommitted ||= new Set(toDelete.map((model) => model.vid)).size > 1;
          await this.daliService.save(
            this.type,
            toDelete.map((model) => ({ ...model, del: true })),
          );
          toDelete.forEach((model) => this.models.delete(model.id));
          mutated = true;
        }
      } catch (error) {
        // A failure after the repair committed leaves the survivor unlinked but the target intact.
        // Flag it so callers surface "repair kept, delete unfinished — retry" rather than "nothing
        // deleted"; a failure before the repair committed rethrows unchanged (a true no-op).
        throw repairCommitted ? new PartialTransactionDeleteError(error) : error;
      } finally {
        // `triggerChanges()` now contains a malformed model per-row — it skips and logs the bad one
        // rather than throwing (see `buildViews` / #5793) — so the refresh can no longer replace an
        // in-flight PartialTransactionDeleteError from the catch with a generic crash. The local
        // try/catch guard this path used to need (#5479 / #5596) is therefore gone.
        if (mutated) {
          this.triggerChanges();
        }
      }

      return true;
    });
  }

  /**
   * Persist transaction views, serialized against every other mutation on this collection (deletes,
   * link/unlink, in-place upserts) via the shared mutation lock. `linkPair`/`unlink` reach the vault
   * through here, so their writes cannot interleave with a concurrent Plaid in-place upsert of the
   * same rows (PR #4783). Both the base save's read (`preserveNick`) and its write run inside the
   * lock; `super.saveToVault` performs the actual (individually Dali-serialized) write.
   */
  override async saveToVault(
    views: IModelView[],
    triggerChanges = false,
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<boolean> {
    return this.runMutationExclusive(() =>
      super.saveToVault(views, triggerChanges, organizationId, collectionIds),
    );
  }

  /**
   * In-place upsert for the Plaid sync. For each incoming row that already exists, preserve the
   * stored cipher (`vid`) and link state (`lTid`/`xLid`/`dc`) by reading `this.models` INSIDE the
   * mutation lock — after any concurrently-queued `linkPair`/`unlink` has committed and before the
   * next one runs — so a user's mid-sync link change is honoured, never silently overwritten by a
   * stale pre-await snapshot (PR #4783). A genuinely new row (no stored model) is saved as-is.
   * Callers set display fields and `srId` on the incoming views; only cipher + link state come from
   * the store.
   */
  async upsertPreservingLinkState(views: TransactionView[]): Promise<boolean> {
    return this.runMutationExclusive(() => {
      const merged = views.map((view) => {
        const model = view.toStoreModel();
        const existing = this.models.get(model.id);
        if (existing) {
          model.vid = existing.vid;
          model.lTid = existing.lTid;
          model.xLid = existing.xLid;
          model.dc = existing.dc;
        }
        return new TransactionView(model);
      });
      return super.saveToVault(merged, true);
    });
  }

  /**
   * Freeze on insert exactly like the base collection — a transaction model must be as immutable in
   * the store as every other model type (its view is already frozen in {@link triggerChanges}). Only
   * count/flag an already-present id as a duplicate on a full vault (re)load (`fromVaultLoad`): the
   * write-back paths (save/saveToVault and the delete repair echo) legitimately re-set existing rows,
   * so logging them was pure noise that also buried a real load-time duplicate in error telemetry.
   */
  loadModels(models: TransactionStoreModel[], fromVaultLoad = false): void {
    const duplicateIds: string[] = [];
    models.forEach((model) => {
      if (this.validator.assertModel(this.type, model)) {
        if (fromVaultLoad && this.models.has(model.id)) {
          // Name the ids: on a genuine vault-load duplicate the last write wins, so the earlier copy
          // is dropped — telemetry needs the ids to find the rows that collided, not just a count.
          duplicateIds.push(model.id);
        }
        this.models.set(model.id, this.deepFreeze(model));
      }
    });
    if (fromVaultLoad && duplicateIds.length > 0) {
      // Sample DISTINCT ids, not raw occurrences: a single id colliding many times would otherwise
      // fill the cap and hide every other colliding id behind the ellipsis. The count stays the total
      // number of dropped copies so the scale of the collision is still visible.
      const distinctIds = [...new Set(duplicateIds)];
      const shown = distinctIds.slice(0, DUPLICATE_ID_REPORT_CAP);
      const summary =
        `Duplicate models on load for type ${this.type}: ${duplicateIds.length} ` +
        `[${shown.join(", ")}${distinctIds.length > DUPLICATE_ID_REPORT_CAP ? ", …" : ""}]`;
      this.log.error(summary);
      // The log above is console-only in prod (ConsoleLogService(false)), so mirror the report to APM
      // to learn whether real vaults actually carry cross-cipher duplicate ids — Low-priority until
      // telemetry shows they exist, then reconcile on load (#5694).
      this.apmService.apm.captureError(new Error(summary));
    }
  }

  clear(): void {
    this.models.clear();
    this._transactionViews.set(null);
  }
}
